import {
  AtsCandidateFormQuestionTypeEnum,
  AtsCandidateFormResponseQuestionTypeEnum,
  AtsDisabilityCandidatesEnum,
  AtsGenderCandidatesEnum,
  AtsOriginCandidatesEnum,
} from '@/generated/graphql/types';
import { z } from 'zod';
import { useTranslationCandidateForm } from '../hooks/use-translations';
import type { TRequiredFields } from '../types';

export const candidateFormSchema = (requiredFields: TRequiredFields) => {
  const { t } = useTranslationCandidateForm();

  const questionOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
  });

  return z.object({
    name: z.string().min(3, t('REQUIRED_FIELD')),
    last_name: z.string().min(3, t('REQUIRED_FIELD')),
    email: z.string().min(1, t('REQUIRED_FIELD')).email(t('INVALID_EMAIL')),
    number_phone: requiredFields.number_phone
      ? z
          .string()
          .min(8, t('REQUIRED_FIELD'))
          .regex(/^\+\d+$/, t('INVALID_PHONE'))
      : z.string().optional().or(z.literal('')),
    country: requiredFields.country
      ? z.string().min(3, t('REQUIRED_FIELD'))
      : z.string().optional().or(z.literal('')),
    resume: requiredFields.resume
      ? z
          .string({
            required_error: t('REQUIRED_FIELD'),
          })
          .superRefine((val, ctx) => {
            if (!val || val.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('REQUIRED_FIELD'),
              });
            }
          })
      : z.string().optional(),
    linkedin_url: requiredFields.linkedin_url
      ? z
          .string()
          .min(3, t('REQUIRED_FIELD'))
          .regex(
            /^https?:\/\/(www\.)?linkedin\.com\/(in|company|school|posts)\/[A-Za-z0-9-_\/%]+\/?(?:\?[^\s#]*)?(?:#[^\s]*)?$/i,
            t('INVALID_LINKEDIN_URL'),
          )
      : z
          .string()
          .regex(
            /^https?:\/\/(www\.)?linkedin\.com\/(in|company|school|posts)\/[A-Za-z0-9-_\/%]+\/?(?:\?[^\s#]*)?(?:#[^\s]*)?$/i,
            t('INVALID_LINKEDIN_URL'),
          )
          .or(z.literal(''))
          .nullable()
          .optional(),
    candidate_source: requiredFields.candidate_source
      ? z.nativeEnum(AtsOriginCandidatesEnum, {
          required_error: t('REQUIRED_FIELD'),
        })
      : z.nativeEnum(AtsOriginCandidatesEnum).optional(),
    disability: requiredFields.disability
      ? z.nativeEnum(AtsDisabilityCandidatesEnum, {
          required_error: t('REQUIRED_FIELD'),
        })
      : z.nativeEnum(AtsDisabilityCandidatesEnum).optional(),
    gender: requiredFields.gender
      ? z.nativeEnum(AtsGenderCandidatesEnum, {
          required_error: t('REQUIRED_FIELD'),
        })
      : z.nativeEnum(AtsGenderCandidatesEnum).optional(),
    answers: z
      .array(
        z.object({
          question_id: z.number(),
          question_type: z.nativeEnum(AtsCandidateFormQuestionTypeEnum),
          question_name: z.string(),
          allow_multiple_responses: z.boolean(),
          question_options: z.array(questionOptionSchema).optional(),
          answer_value: z
            .object({
              value: z.string(),
              is_required: z.boolean(),
              response_size: z
                .nativeEnum(AtsCandidateFormResponseQuestionTypeEnum)
                .optional(),
            })
            .superRefine((data, ctx) => {
              const { value, is_required, response_size } = data;
              if (
                is_required &&
                (!value || value.trim() === '' || value.length === 0)
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t('REQUIRED_FIELD'),
                });
                return;
              }

              if (
                value.length >
                (response_size ===
                AtsCandidateFormResponseQuestionTypeEnum.ShortResponseQuestion
                  ? 70
                  : 700)
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t('MAX_LENGTH_EXCEEDED'),
                });
                return;
              }
            }),
        }),
      )
      .optional()
      .default([]),
  });
};
